import { useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import { useBadges } from '../../contexts/BadgeContext';
import type { BadgeDefinition } from '../../types/badges';
import { ChevronDown, Download, FilePlus, Printer, QrCode, Save } from 'lucide-react';

interface CouponData {
  id: string;
  code: string;
  xpAmount: number;
  badgeId: string;
  pumaPoints?: number;
}

export const CouponGenerator = () => {
  const [numCoupons, setNumCoupons] = useState(9);
  const [xpAmount, setXpAmount] = useState(10);
  const [couponTitle, setCouponTitle] = useState('Positive Behavior Reward');
  const [selectedBadge, setSelectedBadge] = useState<BadgeDefinition | null>(null);
  const [coupons, setCoupons] = useState<CouponData[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [pumaPoints, setPumaPoints] = useState(0);
  
  const { addNotification } = useNotification();
  const { badges: badgeDefinitions } = useBadges();

  const generateRandomCode = () => {
    const characters = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    return result;
  };

  const handleGenerate = () => {
    if (numCoupons <= 0 || numCoupons > 50) {
      addNotification('Please enter a valid number of coupons (1-50)', 'error');
      return;
    }

    if (!selectedBadge) {
      addNotification('Please select a badge', 'error');
      return;
    }
    
    setIsGenerating(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const newCoupons = Array.from({ length: numCoupons }, () => ({
        id: `coupon-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        code: generateRandomCode(),
        xpAmount,
        badgeId: selectedBadge.id
      }));
      
      setCoupons(newCoupons);
      setIsGenerating(false);
      addNotification(`Successfully generated ${numCoupons} coupons`, 'success');
    }, 1000);
  };

  const handlePrint = () => {
    addNotification('Print dialog would open in a real app', 'info');
  };

  const handleSave = () => {
    addNotification('Coupons would be saved to the database in a real app', 'info');
  };

  const handleDownload = () => {
    addNotification('PDF would be downloaded in a real app', 'info');
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold text-gray-800">Coupon Generator</h2>
      <p className="text-gray-600 mb-6">
        Create printable QR code coupons for XP rewards
      </p>
      
      <div className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-6 cursor-pointer"
           onClick={() => setShowInstructions(!showInstructions)}>
        <ChevronDown className="h-4 w-4" />
        <span className="ml-1">Show Instructions</span>
      </div>
      
      <div className="grid grid-cols-4 gap-6 mb-6">
        <div className="col-span-1">
          <label htmlFor="badge" className="block text-sm font-medium text-gray-700 mb-1">
            Select Badge
          </label>
          <select
            id="badge"
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            value={selectedBadge?.id || ''}
            onChange={(e) => setSelectedBadge(badgeDefinitions.find(b => b.id === e.target.value) || null)}
          >
            <option value="">Select a badge...</option>
            {badgeDefinitions.map((badge) => (
              <option key={badge.id} value={badge.id}>{badge.name}</option>
            ))}
          </select>
        </div>
        
        <div className="col-span-1">
          <label htmlFor="numCoupons" className="block text-sm font-medium text-gray-700 mb-1">
            Number of Coupons
          </label>
          <input
            type="number"
            id="numCoupons"
            min="1"
            max="50"
            value={numCoupons}
            onChange={(e) => setNumCoupons(Number(e.target.value))}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            disabled={isGenerating}
          />
        </div>
        
        <div className="col-span-1">
          <label htmlFor="xpAmount" className="block text-sm font-medium text-gray-700 mb-1">
            XP Value
          </label>
          <input
            type="number"
            id="xpAmount"
            min="1"
            max="100"
            value={xpAmount}
            onChange={(e) => setXpAmount(Number(e.target.value))}
            className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            disabled={isGenerating}
          />
        </div>
      </div>
      
      <div className="mb-6">
        <label htmlFor="pumaPoints" className="block text-sm font-medium text-gray-700 mb-1">
          Puma Points (Optional)
        </label>
        <input
          type="number"
          id="pumaPoints"
          min="0"
          value={pumaPoints}
          onChange={(e) => setPumaPoints(Number(e.target.value))}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          disabled={isGenerating}
          placeholder="Enter amount of Puma Points to award"
        />
        <p className="mt-1 text-sm text-gray-500">Leave as 0 if no Puma Points should be awarded</p>
      </div>
      
      <div className="mb-6">
        <label htmlFor="couponDescription" className="block text-sm font-medium text-gray-700 mb-1">
          Coupon Description (Optional)
        </label>
        <input
          type="text"
          id="couponDescription"
          value={couponTitle}
          onChange={(e) => setCouponTitle(e.target.value)}
          className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          disabled={isGenerating}
          placeholder="e.g., Positive Behavior Reward, Homework Pass, etc. (optional)"
        />
      </div>
      
      <div className="col-span-1 flex items-end">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`w-full flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
            isGenerating ? 'opacity-50 cursor-not-allowed' : ''
          }`}
        >
          <FilePlus className="h-4 w-4 mr-2" />
          Generate Coupons
        </button>
      </div>
      
      {showInstructions && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-800 mb-2">Instructions:</h3>
          <ol className="list-decimal list-inside text-gray-700 space-y-1 ml-2">
            <li>Generate a batch of coupons with the desired XP value</li>
            <li>Print the sheet of coupons</li>
            <li>Cut out individual coupons and distribute to students</li>
            <li>Students can redeem coupons by entering the code or scanning the QR code</li>
          </ol>
        </div>
      )}
      
      {coupons.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Generated Coupons</h3>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={handlePrint}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Printer className="h-4 w-4 mr-1" />
                Print
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Save className="h-4 w-4 mr-1" />
                Save
              </button>
              <button
                type="button"
                onClick={handleDownload}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Download className="h-4 w-4 mr-1" />
                PDF
              </button>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden bg-white p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {coupons.map((coupon) => (
                <div
                  key={coupon.id}
                  className="border border-gray-200 rounded-lg p-6 flex flex-col items-center text-center bg-white"
                >
                  <div className="w-32 h-32 mb-4">
                    {selectedBadge && (
                      <img
                        src={Object.values(selectedBadge.levelImages)[0]}
                        alt={`${selectedBadge.name} badge`}
                        className="w-full h-full object-contain"
                      />
                    )}
                  </div>
                  <div className="w-32 h-32 mb-4">
                    <QrCode className="w-full h-full text-gray-800" />
                  </div>
                  <div className="text-2xl font-mono font-bold mb-2 tracking-wider">{coupon.code}</div>
                  <div className="text-xl font-bold mb-4">{xpAmount} XP</div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 text-sm text-gray-500 text-center">
              Print and distribute these coupons to students as rewards for positive behavior.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};